import { useCallback, useRef } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { useSelector } from 'react-redux'
import { useSWRConfig } from 'swr'

import {
  campaignBannerRecipeTileFetcher,
  standardRecipeTileFetcher,
  type CampaignBannerRecipeItem,
  type MenuResponse,
  type MenuSearchResponse,
  type SearchStandardRecipeItem,
  type StandardRecipeItem,
} from '@library/api-menu-experience'
import type { RecipeUUID } from '@library/menu-service'

import { getBasketDate, getBasketMenuUuid, getNumPortions } from 'selectors/basket'

import type { MenuQueryParams } from '../../../context/menuQueryContext'
import { getMenuExperienceMenuSWRKey } from './useMenuExperienceNavigation'

type MenuExperienceRecipeTileRefreshQuery = MenuQueryParams &
  Record<string, unknown> & { collection?: string }

type UseMenuExperienceRecipeTileRefreshArgs = {
  menuId?: string
  menuContent?: MenuResponse['content']
  query: MenuExperienceRecipeTileRefreshQuery
}

type RecipeTileRefreshTargets = {
  campaignBanner: boolean
  standard: boolean
}

type RefreshedRecipeTiles = {
  campaignBanner?: CampaignBannerRecipeItem
  standard?: StandardRecipeItem
}

type RecipeTileRequest = {
  deliveryDate: string
  menuId: string
  numPortions: number
  recipeId: RecipeUUID
}

const replaceStandardRecipeTile = (
  menu: MenuResponse,
  updatedRecipe: StandardRecipeItem,
): MenuResponse => ({
  ...menu,
  content: menu.content.map((contentBlock) =>
    contentBlock.type === 'recipe-grid'
      ? {
          ...contentBlock,
          items: contentBlock.items.map((recipe) =>
            recipe.id === updatedRecipe.id ? updatedRecipe : recipe,
          ),
        }
      : contentBlock,
  ),
})

const replaceCampaignBannerRecipeTile = (
  menu: MenuResponse,
  updatedRecipe: CampaignBannerRecipeItem,
): MenuResponse => ({
  ...menu,
  content: menu.content.map((contentBlock) =>
    contentBlock.type === 'campaign-banner'
      ? {
          ...contentBlock,
          items: contentBlock.items.map((item) =>
            item.type === 'recipe' && item.id === updatedRecipe.id ? updatedRecipe : item,
          ),
        }
      : contentBlock,
  ),
})

const hasCampaignBannerRecipeTile = (content: MenuResponse['content'], recipeId: RecipeUUID) =>
  content.some(
    (contentBlock) =>
      contentBlock.type === 'campaign-banner' &&
      contentBlock.items.some((item) => item.type === 'recipe' && item.id === recipeId),
  )

const getRecipeTileRefreshTargets = (
  content: MenuResponse['content'] | undefined,
  recipeId: RecipeUUID,
): RecipeTileRefreshTargets => ({
  standard: true,
  campaignBanner: Boolean(content && hasCampaignBannerRecipeTile(content, recipeId)),
})

const hasRecipeTileRefreshTarget = ({ campaignBanner, standard }: RecipeTileRefreshTargets) =>
  campaignBanner || standard

const fetchRecipeTiles = async (
  { campaignBanner, standard }: RecipeTileRefreshTargets,
  request: RecipeTileRequest,
  onFailure: () => void,
): Promise<RefreshedRecipeTiles> => {
  const [updatedStandardRecipe, updatedCampaignBannerRecipe] = await Promise.all([
    standard
      ? standardRecipeTileFetcher(request).catch(() => {
          onFailure()

          return undefined
        })
      : undefined,
    campaignBanner
      ? campaignBannerRecipeTileFetcher(request).catch(() => {
          onFailure()

          return undefined
        })
      : undefined,
  ])

  return {
    ...(updatedStandardRecipe && { standard: updatedStandardRecipe }),
    ...(updatedCampaignBannerRecipe && { campaignBanner: updatedCampaignBannerRecipe }),
  }
}

const hasRefreshedRecipeTiles = ({ campaignBanner, standard }: RefreshedRecipeTiles) =>
  Boolean(campaignBanner || standard)

const mergeSearchRecipeTile = (
  recipe: SearchStandardRecipeItem,
  updatedRecipe: StandardRecipeItem,
): SearchStandardRecipeItem => {
  const nextRecipe = recipe.id === updatedRecipe.id ? { ...recipe, ...updatedRecipe } : recipe
  const nextBase = recipe.base ? mergeSearchRecipeTile(recipe.base, updatedRecipe) : undefined

  return nextBase && nextBase !== recipe.base ? { ...nextRecipe, base: nextBase } : nextRecipe
}

const replaceSearchRecipeTile = (
  response: MenuSearchResponse,
  updatedRecipe: StandardRecipeItem,
): MenuSearchResponse =>
  response.type === 'search-landing'
    ? {
        ...response,
        content: response.content.map((contentBlock) =>
          contentBlock.type === 'suggested-recipe'
            ? { ...contentBlock, item: mergeSearchRecipeTile(contentBlock.item, updatedRecipe) }
            : contentBlock,
        ),
      }
    : {
        ...response,
        content: response.content.map((contentBlock) =>
          contentBlock.type === 'search-recipe-results'
            ? {
                ...contentBlock,
                items: contentBlock.items.map((recipe) =>
                  mergeSearchRecipeTile(recipe, updatedRecipe),
                ),
              }
            : contentBlock,
        ),
      }

type MenuExperienceSearchCache = {
  response: MenuSearchResponse
}

const replaceRefreshedRecipeTiles = (
  menu: MenuResponse | undefined,
  { campaignBanner, standard }: RefreshedRecipeTiles,
) => {
  if (!menu) {
    return menu
  }

  const menuWithStandardRecipe = standard ? replaceStandardRecipeTile(menu, standard) : menu

  return campaignBanner
    ? replaceCampaignBannerRecipeTile(menuWithStandardRecipe, campaignBanner)
    : menuWithStandardRecipe
}

export const useMenuExperienceRecipeTileRefresh = ({
  menuId,
  menuContent,
  query,
}: UseMenuExperienceRecipeTileRefreshArgs) => {
  const { mutate } = useSWRConfig()
  const deliveryDate = useSelector(getBasketDate)
  const basketMenuUuid = useSelector(getBasketMenuUuid)
  const basketNumPortions = useSelector(getNumPortions)
  const latestRequestByRecipeId = useRef<Partial<Record<RecipeUUID, symbol>>>({})

  const refreshRecipeTile = useCallback(
    async (recipeId: RecipeUUID, numPortions: number) => {
      const menuSWRKey = getMenuExperienceMenuSWRKey({
        deliveryDate,
        numPortions: basketNumPortions,
        query,
      })

      if (!menuId || !menuSWRKey) {
        return
      }

      const refreshTargets = getRecipeTileRefreshTargets(menuContent, recipeId)

      if (!hasRecipeTileRefreshTarget(refreshTargets)) {
        return
      }

      const requestId = Symbol('menu-experience-recipe-tile-refresh')
      latestRequestByRecipeId.current[recipeId] = requestId

      const logRefreshFailure = () => {
        datadogRum.addAction('menu_experience_recipe_tile_refresh_failed', {
          menu_id: menuId,
          recipe_id: recipeId,
          num_portions: numPortions,
        })
      }

      const refreshedRecipeTiles = await fetchRecipeTiles(
        refreshTargets,
        { recipeId, menuId, numPortions, deliveryDate },
        logRefreshFailure,
      )

      if (latestRequestByRecipeId.current[recipeId] !== requestId) {
        return
      }

      if (!hasRefreshedRecipeTiles(refreshedRecipeTiles)) {
        return
      }

      await Promise.all([
        mutate(
          menuSWRKey,
          (menu: MenuResponse | undefined) =>
            replaceRefreshedRecipeTiles(menu, refreshedRecipeTiles),
          { revalidate: false },
        ),
        refreshedRecipeTiles.standard
          ? mutate(
              (key: unknown) =>
                typeof key === 'object' &&
                key !== null &&
                'key' in key &&
                key.key === 'menu-experience-search' &&
                'input' in key &&
                typeof key.input === 'object' &&
                key.input !== null &&
                'menuId' in key.input &&
                key.input.menuId === basketMenuUuid &&
                'deliveryDate' in key.input &&
                key.input.deliveryDate === deliveryDate &&
                'numPortions' in key.input &&
                key.input.numPortions === basketNumPortions,
              (cached: MenuExperienceSearchCache | undefined) =>
                cached?.response
                  ? {
                      ...cached,
                      response: replaceSearchRecipeTile(
                        cached.response,
                        refreshedRecipeTiles.standard!,
                      ),
                    }
                  : cached,
              { revalidate: (data) => data === undefined },
            )
          : undefined,
      ])
    },
    [basketMenuUuid, basketNumPortions, deliveryDate, menuContent, menuId, mutate, query],
  )

  return { refreshRecipeTile }
}
